(() => {
  const elementName = 'zenit-sticky-purchase';
  if (customElements.get(elementName)) return;

  const consentSelector = '#shopify-pc__banner, [data-zenit-consent-banner]';
  const mobileViewportQuery = '(max-width: 47.99rem)';
  let activeReserveOwner = null;

  class ZenitStickyPurchase extends HTMLElement {
    constructor() {
      super();

      this.handleIntersection = this.handleIntersection.bind(this);
      this.handleSourceMutation = this.handleSourceMutation.bind(this);
      this.handleBarResize = this.handleBarResize.bind(this);
      this.handleConsentResize = this.handleConsentResize.bind(this);
      this.handleConsentDomMutation = this.handleConsentDomMutation.bind(this);
      this.handleViewportResize = this.handleViewportResize.bind(this);
      this.handleViewportScroll = this.handleViewportScroll.bind(this);
      this.handleSubmitClick = this.handleSubmitClick.bind(this);
      this.handleFocusOut = this.handleFocusOut.bind(this);

      this.consentElements = new Set();
      this.consentOffset = 0;
      this.dismissBoundary = null;
      this.requestedVisibility = false;
      this.isBarVisible = false;
      this.pendingHide = false;
      this.reserveFrame = null;
      this.consentFrame = null;
      this.focusFrame = null;
      this.visibilityFrame = null;
    }

    connectedCallback() {
      if (this.isInitialized) return;

      this.productInfo = this.closest('product-info');
      this.sourceControls = this.productInfo?.querySelector(
        '.zenit-product__purchase-controls .product-form__buttons'
      );
      this.sourceButton = this.sourceControls?.querySelector('[id^="ProductSubmitButton-"]');
      this.sourcePrice = this.productInfo?.querySelector(`#price-${this.dataset.sectionId}`);
      this.sourceError = this.sourceButton
        ?.closest('product-form')
        ?.querySelector('.product-form__error-message-wrapper');
      this.stickyButton = this.querySelector('[data-zenit-sticky-submit]');
      this.stickyLabel = this.querySelector('[data-zenit-sticky-label]');
      this.stickySpinner = this.stickyButton?.querySelector('.loading__spinner');
      this.stickyPrice = this.querySelector('[data-zenit-sticky-price]');
      this.stickyError = this.querySelector('[data-zenit-sticky-error]');
      this.dismissBoundary = document.querySelector('[data-zenit-sticky-boundary]');

      if (!this.sourceControls || !this.sourceButton || !this.sourcePrice || !this.stickyButton) {
        this.hidden = true;
        return;
      }

      this.isInitialized = true;
      this.dataset.initialized = 'true';

      this.stickyButton.addEventListener('click', this.handleSubmitClick);
      this.addEventListener('focusout', this.handleFocusOut);
      window.addEventListener('resize', this.handleViewportResize, { passive: true });
      window.addEventListener('scroll', this.handleViewportScroll, { passive: true });
      window.visualViewport?.addEventListener('resize', this.handleViewportResize, { passive: true });
      window.visualViewport?.addEventListener('scroll', this.handleViewportResize, { passive: true });

      this.initializeSourceObserver();
      this.initializeResizeObservers();
      this.initializeConsentObserver();
      this.syncSourceState();
      this.refreshConsentElements();
      this.updateViewportMode();
    }

    disconnectedCallback() {
      if (!this.isInitialized) return;

      this.intersectionObserver?.disconnect();
      this.sourceObserver?.disconnect();
      this.barResizeObserver?.disconnect();
      this.consentResizeObserver?.disconnect();
      this.consentMutationObserver?.disconnect();

      this.stickyButton?.removeEventListener('click', this.handleSubmitClick);
      this.removeEventListener('focusout', this.handleFocusOut);
      window.removeEventListener('resize', this.handleViewportResize);
      window.removeEventListener('scroll', this.handleViewportScroll);
      window.visualViewport?.removeEventListener('resize', this.handleViewportResize);
      window.visualViewport?.removeEventListener('scroll', this.handleViewportResize);

      if (this.reserveFrame) cancelAnimationFrame(this.reserveFrame);
      if (this.consentFrame) cancelAnimationFrame(this.consentFrame);
      if (this.focusFrame) cancelAnimationFrame(this.focusFrame);
      if (this.visibilityFrame) {
        cancelAnimationFrame(this.visibilityFrame);
        this.visibilityFrame = null;
      }

      this.clearRootReserve();
      this.style.removeProperty('--zenit-sticky-purchase-consent-offset');
      this.consentElements.clear();
      this.isInitialized = false;
      this.hidden = true;
      delete this.dataset.initialized;
    }

    initializeSourceObserver() {
      this.sourceObserver = new MutationObserver(this.handleSourceMutation);

      this.sourceObserver.observe(this.sourceButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'aria-haspopup', 'class'],
        childList: true,
        characterData: true,
        subtree: true,
      });

      this.sourceObserver.observe(this.sourcePrice, {
        attributes: true,
        attributeFilter: ['class'],
        childList: true,
        characterData: true,
        subtree: true,
      });

      if (this.sourceError) {
        this.sourceObserver.observe(this.sourceError, {
          attributes: true,
          attributeFilter: ['hidden'],
          childList: true,
          characterData: true,
          subtree: true,
        });
      }
    }

    initializeResizeObservers() {
      if (!window.ResizeObserver) return;

      this.barResizeObserver = new ResizeObserver(this.handleBarResize);
      this.barResizeObserver.observe(this);

      this.consentResizeObserver = new ResizeObserver(this.handleConsentResize);
    }

    initializeConsentObserver() {
      if (!document.body) return;

      this.consentMutationObserver = new MutationObserver(this.handleConsentDomMutation);
      this.consentMutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['hidden', 'aria-hidden', 'class', 'style'],
      });
    }

    handleSourceMutation() {
      if (!this.isInitialized || !this.isConnected) return;
      this.syncSourceState();
    }

    handleBarResize() {
      if (!this.isInitialized || !this.isConnected) return;
      this.scheduleReserveUpdate();
    }

    handleConsentResize() {
      if (!this.isInitialized || !this.isConnected) return;
      this.scheduleConsentRefresh();
    }

    handleConsentDomMutation(mutations) {
      if (!this.isInitialized || !this.isConnected) return;

      const hasConsentChange = mutations.some((mutation) => {
        if (this.isConsentNode(mutation.target)) return true;

        return [...mutation.addedNodes, ...mutation.removedNodes].some((node) => this.isConsentNode(node));
      });

      if (hasConsentChange) this.scheduleConsentRefresh();
    }

    handleViewportResize() {
      if (!this.isInitialized || !this.isConnected) return;
      this.scheduleConsentRefresh();
      this.scheduleReserveUpdate();
    }

    handleViewportScroll() {
      if (
        !this.isInitialized ||
        !this.isConnected ||
        !window.matchMedia(mobileViewportQuery).matches ||
        this.visibilityFrame
      ) {
        return;
      }

      this.visibilityFrame = requestAnimationFrame(() => {
        this.visibilityFrame = null;
        this.handleIntersection();
      });
    }

    handleSubmitClick(event) {
      if (this.stickyButton.disabled || this.stickyButton.getAttribute('aria-disabled') === 'true') {
        event.preventDefault();
      }
    }

    handleFocusOut() {
      if (!this.isInitialized || !this.isConnected || !this.pendingHide) return;

      if (this.focusFrame) cancelAnimationFrame(this.focusFrame);
      this.focusFrame = requestAnimationFrame(() => {
        this.focusFrame = null;
        if (!this.contains(document.activeElement) && !this.requestedVisibility) {
          this.applyVisibility(false);
        }
      });
    }

    handleIntersection(entries) {
      if (!this.isInitialized || !this.isConnected) return;

      const viewportHeight = window.visualViewport?.height || window.innerHeight || document.documentElement.clientHeight;
      const sourceRect = this.sourceControls.getBoundingClientRect();
      const boundaryRect = this.dismissBoundary?.getBoundingClientRect();
      const sourceIsVisible = sourceRect.bottom > 0 && sourceRect.top < viewportHeight;
      const sourceHasPassed = sourceRect.bottom <= 0;
      const boundaryHasStarted = boundaryRect ? boundaryRect.top <= viewportHeight : false;
      const sourceShouldTrigger = window.matchMedia(mobileViewportQuery).matches ? sourceHasPassed : !sourceIsVisible;
      const shouldShow = sourceShouldTrigger && !boundaryHasStarted;

      const visibilityIsCurrent =
        shouldShow === this.requestedVisibility && shouldShow === this.isBarVisible && this.hidden === !shouldShow;

      if (!visibilityIsCurrent) this.requestVisibility(shouldShow);
    }

    updateViewportMode() {
      this.intersectionObserver?.disconnect();

      if (!window.IntersectionObserver) {
        this.requestVisibility(true);
        return;
      }

      this.intersectionObserver = new IntersectionObserver(this.handleIntersection, {
        threshold: 0,
      });
      this.intersectionObserver.observe(this.sourceControls);
      if (this.dismissBoundary) this.intersectionObserver.observe(this.dismissBoundary);
    }

    requestVisibility(shouldShow, force = false) {
      this.requestedVisibility = shouldShow;

      if (!this.requestedVisibility && !force && this.contains(document.activeElement)) {
        this.pendingHide = true;
        return;
      }

      this.pendingHide = false;
      this.applyVisibility(this.requestedVisibility);
    }

    applyVisibility(shouldShow) {
      this.isBarVisible = shouldShow;
      this.hidden = !shouldShow;

      if (shouldShow) {
        this.scheduleReserveUpdate();
      } else {
        this.setRootReserve(0);
      }
    }

    syncSourceState() {
      this.syncButtonState();
      this.syncPriceState();
      this.syncErrorState();
      this.scheduleReserveUpdate();
    }

    syncButtonState() {
      const sourceLabel = this.sourceButton.querySelector(':scope > span');
      const sourceSpinner = this.sourceButton.querySelector('.loading__spinner');
      const isLoading = this.sourceButton.classList.contains('loading');

      this.stickyButton.disabled = this.sourceButton.disabled;
      this.stickyButton.classList.toggle('loading', isLoading);
      this.stickyButton.classList.toggle('button--primary', this.sourceButton.classList.contains('button--primary'));
      this.stickyButton.classList.toggle(
        'button--secondary',
        this.sourceButton.classList.contains('button--secondary')
      );
      this.copyAttribute(this.sourceButton, this.stickyButton, 'aria-disabled');
      this.copyAttribute(this.sourceButton, this.stickyButton, 'aria-haspopup');

      if (isLoading) {
        this.stickyButton.setAttribute('aria-busy', 'true');
      } else {
        this.stickyButton.removeAttribute('aria-busy');
      }

      if (sourceLabel && this.stickyLabel) {
        this.stickyLabel.textContent = sourceLabel.textContent.trim();
      }

      if (this.stickySpinner) {
        const spinnerIsHidden = sourceSpinner ? sourceSpinner.classList.contains('hidden') : !isLoading;
        this.stickySpinner.classList.toggle('hidden', spinnerIsHidden);
      }
    }

    syncPriceState() {
      if (!this.stickyPrice) return;

      if (this.stickyPrice.innerHTML !== this.sourcePrice.innerHTML) {
        this.stickyPrice.innerHTML = this.sourcePrice.innerHTML;
      }

      this.stickyPrice.toggleAttribute('hidden', this.sourcePrice.classList.contains('hidden'));
    }

    syncErrorState() {
      if (!this.stickyError) return;

      const errorMessage = this.sourceError?.querySelector('.product-form__error-message')?.textContent.trim() || '';
      const shouldShowError = Boolean(errorMessage) && !this.sourceError?.hasAttribute('hidden');

      this.stickyError.textContent = errorMessage;
      this.stickyError.toggleAttribute('hidden', !shouldShowError);
    }

    copyAttribute(source, destination, attributeName) {
      const value = source.getAttribute(attributeName);

      if (value === null) {
        destination.removeAttribute(attributeName);
      } else {
        destination.setAttribute(attributeName, value);
      }
    }

    isConsentNode(node) {
      if (!(node instanceof Element)) return false;

      return Boolean(
        node.matches(consentSelector) || node.closest(consentSelector) || node.querySelector(consentSelector)
      );
    }

    scheduleConsentRefresh() {
      if (this.consentFrame) return;

      this.consentFrame = requestAnimationFrame(() => {
        this.consentFrame = null;
        this.refreshConsentElements();
      });
    }

    refreshConsentElements() {
      const currentElements = new Set(document.querySelectorAll(consentSelector));

      if (this.consentResizeObserver) {
        this.consentElements.forEach((element) => {
          if (!currentElements.has(element)) this.consentResizeObserver.unobserve(element);
        });

        currentElements.forEach((element) => {
          if (!this.consentElements.has(element)) this.consentResizeObserver.observe(element);
        });
      }

      this.consentElements = currentElements;
      this.updateConsentOffset();
    }

    updateConsentOffset() {
      const viewportTop = window.visualViewport?.offsetTop || 0;
      const viewportHeight =
        window.visualViewport?.height || window.innerHeight || document.documentElement.clientHeight;
      const viewportBottom = viewportTop + viewportHeight;
      let nextOffset = 0;

      this.consentElements.forEach((element) => {
        if (!this.isVisibleConsentElement(element)) return;

        const rect = element.getBoundingClientRect();
        if (rect.bottom <= viewportTop + viewportHeight / 2 || rect.top >= viewportBottom) return;

        nextOffset = Math.max(nextOffset, Math.ceil(viewportBottom - Math.max(viewportTop, rect.top)));
      });

      this.consentOffset = nextOffset;
      this.style.setProperty('--zenit-sticky-purchase-consent-offset', `${nextOffset}px`);
      this.scheduleReserveUpdate();
    }

    isVisibleConsentElement(element) {
      if (!element.isConnected || element.hidden || element.getAttribute('aria-hidden') === 'true') return false;

      const styles = window.getComputedStyle(element);
      const rect = element.getBoundingClientRect();

      return (
        styles.display !== 'none' &&
        styles.visibility !== 'hidden' &&
        styles.visibility !== 'collapse' &&
        Number.parseFloat(styles.opacity || '1') > 0 &&
        rect.width > 0 &&
        rect.height > 0 &&
        rect.bottom > 0
      );
    }

    scheduleReserveUpdate() {
      if (this.reserveFrame) return;

      this.reserveFrame = requestAnimationFrame(() => {
        this.reserveFrame = null;
        this.updateRootReserve();
      });
    }

    updateRootReserve() {
      if (!this.isBarVisible || !this.isConnected) {
        this.setRootReserve(0);
        return;
      }

      const barHeight = Math.ceil(this.getBoundingClientRect().height);
      const reserve = barHeight > 0 ? barHeight + this.consentOffset : 0;
      this.setRootReserve(reserve);
    }

    setRootReserve(reserve) {
      const root = document.documentElement;

      if (reserve > 0) {
        activeReserveOwner = this;
        root.style.setProperty('--zenit-sticky-purchase-reserve', `${reserve}px`);
        root.classList.add('zenit-sticky-purchase-active');
        return;
      }

      if (activeReserveOwner === this) this.clearRootReserve();
    }

    clearRootReserve() {
      if (activeReserveOwner !== this) return;

      const root = document.documentElement;
      root.style.removeProperty('--zenit-sticky-purchase-reserve');
      root.classList.remove('zenit-sticky-purchase-active');
      activeReserveOwner = null;
    }
  }

  customElements.define(elementName, ZenitStickyPurchase);
})();
